import { createSelector } from '@reduxjs/toolkit';
import { EmptyCatalog } from '@/types/Catalog';
import { EmptyCoverLot } from '@/types/CoverLot';
import { GlobalState } from '@/redux/rootReducer';
import { shouldShowLive } from '@/utils/live';
import ms from 'ms';

export const CATALOG_REDUX_STORE_TIME = ms('30m');

const stateSelector = (state: Partial<GlobalState>) => state.catalog;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getIsCatalogLoaded = createSelector([loadedSelector, idSelector], (loaded, id) =>
    loaded.hasOwnProperty(id)
);

export const getCatalog = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || EmptyCatalog);

export const getCatalogPreview = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId?.[id]?.publishDateTs === 0 && byId?.[id].catalogStatus !== 'done'
);
export const getCatalogIsLAPayments = createSelector(getCatalog, (catalog) => catalog?.liveAuctioneersPayments);
export const getCatalogPaymentProvider = createSelector(getCatalog, (catalog) => catalog?.paymentProvider);
export const getIsCatalogAutopay = createSelector(getCatalog, (catalog) => catalog?.isAutopay);
export const getCatalogCoverLots = createSelector(
    getCatalog,
    (catalog) => catalog?.coverLots || [EmptyCoverLot, EmptyCoverLot, EmptyCoverLot]
);

export const getShouldShowLive = createSelector([byIdSelector, idSelector], (byId, id) => {
    if (byId[id]) {
        const { catalogStatus, isCatalogOnly, saleStartTs } = byId[id];
        return shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    }
    return false;
});

export const getAllCatalogs = createSelector([byIdSelector], (byId) => byId);

export const getCatalogIdsForSeller = createSelector([byIdSelector, idSelector], (byId, id) =>
    Object.keys(byId)
        .map((key) => byId[Number(key)])
        .filter((catalog) => catalog?.sellerId === id && catalog?.catalogStatus)
        .map((catalog) => catalog.catalogId)
);

export const whichCatalogsNeeded = createSelector(
    [byIdSelector, loadedSelector, loadingSelector, idSelector],
    (ids, loaded, loading, catalogIds) => {
        const needed: number[] = [];
        catalogIds.forEach((catalogId: number) => {
            if (catalogId) {
                const time = Date.now();
                const diff = time - loaded[catalogId];
                if (diff < CATALOG_REDUX_STORE_TIME) {
                    return false;
                }
                if (!loading.includes(catalogId)) {
                    needed.push(catalogId);
                }
            }
        });
        return needed;
    }
);
